<template>
  <v-card class="trezo-card bg-white border-0 shadow-none border-radius mb-0">
    <div class="v-card-header">
      <h5 class="mb-0">Kullanıcı Token Tüketimi</h5>
    </div>
    <div class="p-3">
      <div v-if="!breakdown.length" class="text-muted py-4 text-center">
        Veri bulunamadı
      </div>
      <template v-else>
        <!-- Bar chart -->
        <apexchart
          type="bar"
          :height="chartHeight"
          :options="chartOptions"
          :series="chartSeries"
        />

        <!-- Tablo -->
        <v-data-table
          :headers="headers"
          :items="breakdown"
          density="compact"
          class="mt-4"
          :items-per-page="-1"
        >
          <template #item.name="{ item }">
            <router-link
              :to="`/admin/users/${item.username}/questions`"
              class="text-decoration-none font-weight-medium"
              style="color: #1a5f4a"
            >
              {{ item.name }}
            </router-link>
          </template>
          <template #item.period_tokens="{ item }">
            {{ item.period_tokens.toLocaleString('tr-TR') }}
          </template>
          <template #item.cost_usd="{ item }">
            <span class="font-weight-medium">${{ (item.cost_usd ?? 0).toFixed(2) }}</span>
          </template>
          <template #item.avg_rating="{ item }">
            <span v-if="item.avg_rating != null" class="d-flex align-center gap-1">
              <v-icon size="14" color="amber-darken-1">mdi-star</v-icon>
              <span class="text-body-2 font-weight-medium">{{ Number(item.avg_rating).toFixed(1) }}</span>
            </span>
            <span v-else class="text-medium-emphasis text-body-2">—</span>
          </template>
          <template #item.quota_pct="{ item }">
            <div class="d-flex align-center gap-2">
              <v-progress-linear
                :model-value="item.quota_pct"
                :color="quotaColor(item.quota_pct)"
                height="8"
                rounded
                style="min-width: 80px"
              />
              <span class="text-body-2">{{ item.quota_pct }}%</span>
            </div>
          </template>
        </v-data-table>
        <p class="text-caption text-medium-emphasis mt-2">
          * Dönem Tokeni: seçili dönemdeki tüketim | Toplam Kota %: tüm zamana ait kota kullanımı
        </p>
      </template>
    </div>
  </v-card>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { ApexOptions } from 'apexcharts'
import type { UserTokenBreakdown } from '@/services/adminStatsApi'

const props = defineProps<{ breakdown: UserTokenBreakdown[] }>()

const chartHeight = computed(() => Math.max(220, props.breakdown.length * 36))

const chartOptions = computed<ApexOptions>(() => ({
  chart: { toolbar: { show: false } },
  plotOptions: { bar: { horizontal: true, barHeight: '60%' } },
  colors: ['#1a5f4a'],
  dataLabels: { enabled: false },
  xaxis: {
    categories: props.breakdown.map(u => u.name),
    labels: { formatter: (v: string) => '$' + Number(v).toFixed(2) },
  },
  tooltip: { y: { formatter: (v: number) => '$' + v.toFixed(2) } },
}))

const chartSeries = computed(() => [{
  name: 'Maliyet (USD)',
  data: props.breakdown.map(u => u.cost_usd),
}])

const headers = [
  { title: 'Ad Soyad', key: 'name' },
  { title: 'Kullanıcı Adı', key: 'username' },
  { title: 'Dönem Tokeni', key: 'period_tokens' },
  { title: 'Maliyet (USD)', key: 'cost_usd' },
  { title: 'Ort. Puan', key: 'avg_rating' },
  { title: 'Toplam Kota %', key: 'quota_pct' },
]

function quotaColor(pct: number): string {
  if (pct > 80) return 'error'
  if (pct > 50) return 'warning'
  return 'success'
}
</script>
